/*
  * transaction-viewer specific options
      var options = buildOptions([
        // logos are hosted on Card Tokenisation page
        ['logo', window.__env.verifylogo, LITTLEPAY_API.verify + '/assets/images/logo.png'], DONE
        ['name', window.__env.verifyname, $scope.translate('LITTLEPAY-MY-TRIPS')], DEPRECATED
        ['color', window.__env.verifycolor, '#1DBD72'], DONE
        ['headercolor', window.__env.verifyheadercolor, undefined], DONE
        ['btncolor', window.__env.verifybtncolor, undefined], DONE
        ['btntextcolor', window.__env.verifybtntextcolor, undefined], DONE
        ['backgroundcolor', window.__env.verifybackgroundcolor, undefined], DONE
        ['bodytextcolor', window.__env.verifybodytextcolor, undefined], DEPRECATED
        // multiple
        TODO only set for first-group in transaction-viewer. Favicons are only shown in the browser tab. Since we only use iframe, this is probably needed.
        ['favicons', window.__env.verifyfavicons, undefined],
        ['documenttitle', window.__env.verifydocumenttitle ?($scope.translate(window.__env.verifydocumenttitle)).toString() : undefined, undefined], DEPRECATED
        // string of only one locale
        ['language', configAuth.getSelectedLocale(), 'en'], DONE
        // array of card types
        TODO should fail validation if card number not in cardTypes. ['visa', 'mastercard', 'maestro'] is default, "[all]" is special case for all card types.
        ['cardTypes', window.__env.verifycardtypes, ['visa', 'mastercard', 'maestro']],
      ]);
 */

import {
  CardType,
  defaultCardTypes,
  supportedCardTypes,
} from "card-capture-app/src/app/model/card.model";
import { BaseTheme, validateTheme } from "card-capture-app/src/app/model/theme.model";
import { supportedXpayMethods, XpayMethod } from "./xpayMethod.model.ts";

// These are the theme options used in Card Tokenisation
const cardTokenisationColorKeys = [
  "color",
  "headercolor",
  "btncolor",
  "btntextcolor",
  "backgroundcolor",
  "validationmessagecolor",
  "loadingscreenbackgroundcolor",
  "loadingscreencolor",
  "foregroundcolor",
] as const;

export type CardTokenisationColorKey = (typeof cardTokenisationColorKeys)[number];

export type CardTokenisationTheme = {
  [key in CardTokenisationColorKey]?: string;
};

export type CardHolderDetailFieldsOption = "HIDE" | "DISPLAY" | "DISPLAY_IF_REQUIRED" | "EMAIL";

export type UIMode = "FULL" | "COMPACT";

export type AuthFieldsRequiredOption = "CVV" | "CVV_CHNAME";

export type StoredPaymentMethod = {
  id: string;
  cardExpiry: string;
  // TODO narrow down card types
  cardType: string;
  cardMask: string;
  requiresAuthentication?: boolean;
  authFieldsRequired?: AuthFieldsRequiredOption;
};

export type CardCaptureOptions = {
  logo?: string;
  name?: string; // DEPRECATED
  favicons?: string; // DEPRECATED
  documenttitle?: string; // DEPRECATED
  language?: string;
  panonlymode?: boolean;
  allowexpiredcardsmode?: boolean;
  targetiframename?: string;
  address?: {
    address1?: string;
    address2?: string;
    town_city?: string;
    postcode?: string;
    country?: string;
  };
  cardTypes?: CardType[];
  skipAuth?: boolean;
  threeDSReAuth?: boolean;
  // Hides the "Save card" checkbox, e.g. when registering a card for merchant-initiated transactions
  disableSavingCards?: boolean;
  authenticationId?: string;
  // control the display of the additional cardholder details fields (email/phone)
  cardholderDetailFields?: CardHolderDetailFieldsOption;
  // An array of supported payment-methods, if present unified-tokenisation-app will be displayed
  paymentMethods?: XpayMethod[];
  uiMode?: UIMode;
  theme?: BaseTheme;

  // options for the stripe card migration
  storedPaymentMethod?: StoredPaymentMethod;
} & CardTokenisationTheme;

export interface CallbackResponse {
  status: number;
  message: string;
}

export interface TokeniseResponse extends CallbackResponse {
  token: string;
  saveCard?: boolean;
}

export type CardCaptureCallbacks = {
  onTokenize?: (response: TokeniseResponse) => void;
  onVerificationFailure?: (response: CallbackResponse) => void;
  onError?: (response: CallbackResponse) => void;
  onSubmit?: () => void;
  onPageLoaded?: () => void;
  onCancel?: () => void;
};

export type CardCaptureLibConfig = {
  readonly authorization: string;
  readonly element: string;
  readonly envUrl: URL;
  readonly options: CardCaptureOptions;
} & CardCaptureCallbacks;

export function validateCardCaptureLibConfig(config: unknown): CardCaptureLibConfig {
  if (!config || typeof config !== "object") {
    throw new Error("littlepay() requires a configuration object");
  }

  // Type assertion to treat params as an object
  const obj = config as { [key: string]: unknown };

  const element = validateString(obj.element, "element");
  const authorization = validateString(obj.authorization, "authorization");
  let envUrl = validateEnvironmentURL(obj.envUrl);
  const options = validateCardCaptureOptions(obj.options);

  envUrl = selectApplicationUrl(envUrl, options);
  // TODO add more validation rules

  return {
    authorization,
    element,
    envUrl,
    options,
    onTokenize: validateFunction<() => void>(obj.onTokenize, "onTokenize"),
    onVerificationFailure: validateFunction<() => void>(
      obj.onVerificationFailure,
      "onVerificationFailure",
    ),
    onError: validateFunction<() => void>(obj.onError, "onError"),
    onSubmit: validateFunction<() => void>(obj.onSubmit, "onSubmit"),
    onCancel: validateFunction<() => void>(obj.onCancel, "onCancel"),
    onPageLoaded: validateFunction<() => void>(obj.onPageLoaded, "onPageLoaded"),
  };
}

export function validateFunction<T>(fun: unknown, name: string) {
  if (fun && typeof fun !== "function") {
    console.warn(`${name} must be a function`);
    return undefined;
  }
  return fun as T;
}

export function validateString(value: unknown, key: string): string {
  if (typeof value !== "string") {
    throw new Error(`${key} must be a string`);
  }
  return value;
}

export function validateEnvironmentURL(envURLString: unknown): URL {
  if (!envURLString) {
    throw new Error("envUrl property is required");
  }
  if (typeof envURLString !== "string") {
    throw new Error("envUrl property must be a string");
  }

  let envURL: URL;
  try {
    envURL = new URL(envURLString);
  } catch (e) {
    throw new Error(`envUrl ${envURLString} is not a valid card-capture URL`);
  }

  if (
    [
      "https://card-capture.dev.littlepay.com",
      "https://card-capture.qa.littlepay.com",
      "https://card-capture.littlepay.com",
      "http://localhost",
    ]
      .map((allowedURLString) => new URL(allowedURLString))
      .filter((allowedURL) => {
        return envURL.hostname === allowedURL.hostname && envURL.protocol === allowedURL.protocol;
      }).length <= 0
  ) {
    throw new Error(`envUrl ${envURLString} is not a valid card-capture URL`);
  }
  return envURL;
}

export function validateCardCaptureOptions(input: unknown): CardCaptureOptions {
  const options: CardCaptureOptions = {};
  if (!input) {
    return options;
  }

  if (typeof options !== "object") {
    console.warn("options must be an object");
    return options;
  }

  const obj = input as { [key: string]: unknown };

  options.logo = validateURLString(obj.logo, "logo");
  validateColorOptions(options, obj);
  options.language = obj.language ? validateString(obj.language, "language") : undefined;
  // should move to parent config object because it is not a card capture lib concern
  options.targetiframename = obj.targetiframename
    ? validateString(obj.targetiframename, "targetiframename")
    : undefined;

  // TODO add more validation rules
  options.allowexpiredcardsmode = validateBoolean(
    obj.allowexpiredcardsmode,
    "allowexpiredcardsmode",
    false,
  );

  options.cardTypes = validateCardTypes(obj.cardTypes);
  options.skipAuth = validateBoolean(obj.skipAuth, "skipAuth", false);
  options.threeDSReAuth = validateBoolean(obj.threeDSReAuth, "threeDSReAuth", false);
  options.disableSavingCards = validateBoolean(obj.disableSavingCards, "disableSavingCards", false);
  options.authenticationId = obj.authenticationId
    ? validateString(obj.authenticationId, "authenticationId")
    : undefined;
  options.cardholderDetailFields = validateCardHolderDetailFields(obj.cardholderDetailFields);
  options.paymentMethods = validatePaymentMethods(obj.paymentMethods);
  options.uiMode = validateUIMode(obj.uiMode);
  options.theme = validateTheme(obj.theme);

  options.storedPaymentMethod = validateStoredPaymentMethod(obj.storedPaymentMethod);

  return options;
}

function validateUIMode(uiMode: unknown): UIMode | undefined {
  if (!uiMode) {
    return "FULL";
  }
  if (uiMode === "FULL" || uiMode === "COMPACT") {
    return uiMode;
  }
  console.warn("uiMode must be FULL or COMPACT");
  return "FULL";
}

function validateCardTypes(value: unknown): CardType[] {
  if (!Array.isArray(value) || value.length === 0) {
    console.warn("cardTypes must be an array of supported card types");
    return defaultCardTypes;
  }

  if (value.length === 1 && value[0] === "all") {
    return supportedCardTypes.map((cardType) => cardType);
  }

  if (!value.every((item) => supportedCardTypes.includes(item))) {
    console.warn("cardTypes must be an array of supported card types");
    return defaultCardTypes;
  }

  return value;
}

function validatePaymentMethods(value: unknown) {
  if (!value) {
    return undefined;
  }

  if (!Array.isArray(value)) {
    throw new Error("PaymentMethods must be an array of supported payment methods");
  }

  const allowedValues = new Set<string>(supportedXpayMethods);

  const invalidValues = value.filter((v) => !allowedValues.has(v));

  if (invalidValues.length > 0) {
    throw new Error(
      `Invalid tokenisation methods found: ${invalidValues.join(
        ", ",
      )}. Allowed values are: ${supportedXpayMethods.join(", ")}`,
    );
  }

  return value;
}

function validateBoolean(value: unknown, key: string, defaultValue: boolean): boolean {
  if (value === undefined) {
    return defaultValue;
  }

  if (typeof value !== "boolean") {
    console.warn(key, "must be boolean");
    return defaultValue;
  }

  return value;
}

export function validateURLString(url: unknown, key: string) {
  if (!url) {
    return undefined;
  }

  if (typeof url !== "string") {
    console.warn(`${key} must be a string`);
    return undefined;
  }
  try {
    return new URL(url).toString();
  } catch (e) {
    console.warn(`${key} must be a valid URL`);
  }
  return undefined;
}

// Scans for color properties in a given object and assigns them to the card capture options after validating them
function validateColorOptions(options: CardCaptureOptions, obj: { [key: string]: unknown }) {
  cardTokenisationColorKeys.forEach((key) => {
    options[key] = validateColor(obj[key], key);
  });
}

const PATTERN_COLOR_HEX = /^#[0-9A-F]{6}$/;
const PATTERN_COLOR_RGB = /^rgb\(\d{1,3},\d{1,3},\d{1,3}\)$/;

const PATTERN_COLORS: RegExp = new RegExp(
  [PATTERN_COLOR_HEX.source, PATTERN_COLOR_RGB.source].join("|"),
  "i",
);

export function validateColor(color: unknown, key: string) {
  if (!color) {
    return undefined;
  }
  if (typeof color !== "string") {
    console.warn(`${key} must be a string`);
    return undefined;
  }

  const colorWithoutSpaces = color.replace(/\s/g, "");

  if (!PATTERN_COLORS.test(colorWithoutSpaces)) {
    console.warn(`${key} must be a valid hex or rgb() color`);
    return undefined;
  }
  return color;
}

function validateCardHolderDetailFields(value: unknown): CardHolderDetailFieldsOption | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value !== "string") {
    console.warn("cardholderDetailFields must be a string");
    return undefined;
  }

  const supportedValues: CardHolderDetailFieldsOption[] = [
    "HIDE",
    "DISPLAY",
    "DISPLAY_IF_REQUIRED",
    "EMAIL",
  ];
  if (!supportedValues.includes(value as CardHolderDetailFieldsOption)) {
    console.warn("cardholderDetailFields must be one of HIDE, DISPLAY, DISPLAY_IF_REQUIRED, EMAIL");
    return undefined;
  }

  return value as CardHolderDetailFieldsOption;
}

function validateCardExpiryFormat(expiry: string): boolean {
  return /^\d{2}\/\d{2}$/.test(expiry);
}

function validateCardMaskFormat(mask: string): boolean {
  return /^\d{4}$/.test(mask);
}

export function validateAuthFieldsRequired(value: unknown): AuthFieldsRequiredOption | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value !== "string" || (value !== "CVV" && value !== "CVV_CHNAME")) {
    throw new Error("authFieldsRequired only supports 'CVV' or 'CVV_CHNAME'");
  }

  return value as AuthFieldsRequiredOption;
}

/**
 * If requiresAuthentication is false, should exit with an error and meaningful message (the errors shown in the new card details form)
 * If requiresAuthentication is true, authFieldsRequired is blank, should progress directly to authentication. (very rare case will then directly proceed to authentication)
 * If requiresAuthentication is true, authFieldsRequired is CVV, card capture will hide the cardholder name fileld and only ask for CVV.
 * If requiresAuthentication is true, authFieldsRequired is CVV_CHNAME, card capture will ask for both CVV and Cardholder name.
 * @param value
 * @returns
 */
export function validateStoredPaymentMethod(value: unknown): StoredPaymentMethod | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value !== "object") {
    throw new TypeError("storedPaymentMethod must be an object");
  }

  const obj = value as Record<string, unknown>;
  const {
    id,
    cardExpiry,
    cardMask,
    cardType,
    requiresAuthentication,
    authFieldsRequired: rawAuthFields, // Rename for clarity
  } = obj;

  // Validate fields individually, throw errors if invalid
  if (typeof id !== "string" || id.trim().length === 0) {
    throw new Error("storedPaymentMethod.id must be a non-empty string");
  }
  if (typeof cardExpiry !== "string" || cardExpiry.trim().length === 0) {
    throw new Error("storedPaymentMethod.cardExpiry must be a non-empty string");
  }
  if (!validateCardExpiryFormat(cardExpiry.trim())) {
    throw new Error("storedPaymentMethod.cardExpiry must be in MM/YY format");
  }
  if (typeof cardMask !== "string" || cardMask.trim().length === 0) {
    throw new Error("storedPaymentMethod.cardMask must be a non-empty string");
  }
  if (!validateCardMaskFormat(cardMask.trim())) {
    throw new Error("storedPaymentMethod.cardMask must be a 4-digit number");
  }
  if (typeof cardType !== "string" || cardType.trim().length === 0) {
    throw new Error("storedPaymentMethod.cardType must be a non-empty string");
  }
  if (!supportedCardTypes.includes(cardType as CardType)) {
    throw new Error(
      `storedPaymentMethod.cardType must be one of: ${supportedCardTypes.join(", ")}`,
    );
  }
  if (typeof requiresAuthentication !== "boolean") {
    throw new TypeError("storedPaymentMethod.requiresAuthentication must be a boolean");
  }
  if (requiresAuthentication === false) {
    throw new Error(
      "storedPaymentMethod.requiresAuthentication should be true in order to proceed with authentication",
    );
  }

  const authFieldsRequired =
    rawAuthFields === undefined ? undefined : validateAuthFieldsRequired(rawAuthFields);

  const result = {
    id,
    cardExpiry,
    cardMask,
    cardType,
    requiresAuthentication,
  };

  if (authFieldsRequired) {
    return { ...result, authFieldsRequired };
  }

  return result;
}

export function selectApplicationUrl(envUrl: URL, options: CardCaptureOptions): URL {
  if (options?.paymentMethods) {
    envUrl.pathname = "/unified-tokenisation/index.html";
  }

  return envUrl;
}
